import { useState, useEffect } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import { X, Send, Star } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const initialForm = { first_name: "", last_name: "", testimonial: "" };

export default function ReviewModal({ open, onClose }) {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [rating, setRating] = useState(5);

  useEffect(() => {
    if (open) document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/testimonials`, {
        first_name: form.first_name,
        last_name: form.last_name,
        testimonial: `★${rating}/5 — ${form.testimonial}`,
      });
      toast.success("Thank you! Your review has been submitted.");
      setForm(initialForm);
      setRating(5);
      onClose();
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 bg-foreground/60 backdrop-blur-sm z-[60]"
            data-testid="review-modal-backdrop"
          />
          <motion.div
            initial={{ opacity: 0, y: 30, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-[70] flex items-center justify-center p-4 md:p-8 pointer-events-none"
          >
            <div className="bg-background w-full max-w-2xl max-h-[90vh] overflow-y-auto pointer-events-auto" data-testid="review-modal">
              <div className="sticky top-0 bg-background border-b border-foreground/15 flex items-center justify-between px-6 md:px-10 py-5 z-10">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary mb-1">Share Your Story</div>
                  <h3 className="font-display text-3xl md:text-4xl uppercase leading-none">Write a Review</h3>
                </div>
                <button
                  onClick={onClose}
                  data-testid="review-modal-close"
                  aria-label="Close"
                  className="p-2 hover:text-primary transition-colors"
                >
                  <X size={22} />
                </button>
              </div>

              <form onSubmit={onSubmit} className="px-6 md:px-10 py-8 space-y-6" data-testid="review-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Field label="First Name" name="first_name" value={form.first_name} onChange={onChange} required testid="review-first-name" />
                  <Field label="Last Name" name="last_name" value={form.last_name} onChange={onChange} required testid="review-last-name" />
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground mb-3">Rating</label>
                  <div className="flex items-center gap-2" data-testid="review-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setRating(n)}
                        data-testid={`review-star-${n}`}
                        aria-label={`${n} star${n === 1 ? "" : "s"}`}
                        className="p-1"
                      >
                        <Star size={28} className={n <= rating ? "text-primary fill-primary" : "text-foreground/30"} />
                      </button>
                    ))}
                    <span className="ml-3 text-xs font-bold uppercase tracking-[0.25em] text-muted-foreground">{rating} / 5</span>
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground mb-2">Your Review</label>
                  <textarea
                    name="testimonial"
                    value={form.testimonial}
                    onChange={onChange}
                    required
                    rows={6}
                    minLength={20}
                    placeholder="Tell us about your experience with BeGreat..."
                    data-testid="review-textarea"
                    className="w-full bg-transparent border-2 border-foreground/20 focus:border-primary outline-none p-4 text-base resize-none"
                  />
                  <p className="mt-2 text-xs text-muted-foreground">Reviews are sent to the BeGreat team for approval before being published.</p>
                </div>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="review-submit"
                  className="group inline-flex items-center gap-3 bg-foreground text-background px-7 py-4 text-xs font-bold uppercase tracking-[0.25em] hover:bg-primary disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {submitting ? "Sending..." : "Submit Review"}
                  <Send size={14} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}

function Field({ label, name, value, onChange, required, type = "text", testid }) {
  return (
    <div>
      <label className="block text-[10px] font-bold uppercase tracking-[0.3em] text-muted-foreground mb-2">{label}</label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        data-testid={testid}
        className="w-full bg-transparent border-0 border-b-2 border-foreground/25 focus:border-primary outline-none py-3 text-base"
      />
    </div>
  );
}
